<template>
  <div class="questionnaire-component-container">
    <transition-group
      v-if="clippedQuestions.length > 1"
      tag="div"
      name="slide"
      :class="{ back: back }"
      class="content-container"
    >
      <div
        v-for="(question, index) in clippedQuestions"
        :key="question.id"
        :class="{ right: index > 1 }"
        class="content"
      >
        <slot
          v-if="question.id === 'completed'"
          name="completed"
        />
        <div
          v-else-if="['start', 'end'].includes(question.id)"
          :class="{ empty: isEmpty(question) }"
          class="box"
        />
        <div
          v-else
          class="box"
        >
          <div
            class="question"
            v-html="question.question"
          />
          <div v-if="question.type === 'Date Picker'">
            <date-picker
              :date="dateFormat(question)"
              :hide-label="true"
              @date-change="(...args) => dateChange(question, ...args)"
            />
            <mastery-button
              :disabled="!dateValid(question)"
              class="date-submit"
              @mastery-button-clicked="answerDateQuestion(question)"
            >
              SUBMIT
            </mastery-button>
          </div>
          <div
            v-else
            :class="{
              single:
                question.answers.length < 5 && question.answers.length > 2,
            }"
            class="answer-container"
          >
            <template v-for="answer in sortedAnswers(question)" :key="answer.id">
              <input
                :id="`answer-${answer.id}`"
                :disabled="false"
                :checked="isAnswerSelected(answer)"
                :value="answer"
                type="checkbox"
                @change="answerQuestion(answer)"
              />
              <label
                :for="`answer-${answer.id}`"
                :class="{ selected: isAnswerSelected(answer) }"
              >
                <div
                  class="answer-text"
                  v-html="answer.content"
                />
              </label>
            </template>
          </div>
        </div>
        <button
          v-if="showBack(question)"
          class="back"
          @click="handleBack"
        >
          <icon
            class="arrow-icon"
            name="mastery-arrow-left"
            variant="b2b2b2"
          />
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
import DatePicker from '@components/singletons/date-picker';
import Icon from '@components/singletons/icon';
import MasteryButton from '@components/ui/masteryButton';

export default {
  components: {
    DatePicker,
    Icon,
    MasteryButton,
  },
  props: {
    allowBack: {
      type: Boolean,
      default: true,
    },
    clippedQuestions: {
      type: Array,
    },
    step: {
      type: Number,
    },
  },
  emits: ['answer-question', 'answer-date-question', 'back-clicked'],
  data() {
    return {
      back: false,
      dates: [],
      selectedAnswerIds: [],
    };
  },
  mounted() {
    this.$watch('step', (newStep, oldStep) => {
      if (newStep < oldStep) {
        // Set back for transition
        this.back = true;
        // Reset answers
        this.resetState();
      }
    });
  },
  methods: {
    answerQuestion(answer) {
      const currentQuestion = this.clippedQuestions[1];
      const allowedAnswers = currentQuestion.answers.filter((a) => a.correct)
        .length;
      const answers = [...this.selectedAnswerIds, answer.id];
      const validSelectedAnswers = currentQuestion.answers.filter((answer) =>
        answers.includes(answer.id),
      );
      if (validSelectedAnswers.length <= allowedAnswers) {
        this.selectedAnswerIds = validSelectedAnswers.map((a) => a.id);
      }
      if (validSelectedAnswers.length === allowedAnswers) {
        this.back = false;
        this.$emit('answer-question', currentQuestion, validSelectedAnswers);
      }
    },
    answerDateQuestion(question) {
      this.back = false;
      const questionDate = this.getQuestionDate(question);
      this.$emit('answer-date-question', question, questionDate.date);
    },
    dateChange(question, ...args) {
      const questionDate = this.getQuestionDate(question);
      const [key, value] = args;

      questionDate.date[key] = value;
      this.dates = [...this.dates.filter(d => d.questionId !== question.id), questionDate];
    },
    dateFormat(question) {
      const questionDate = this.getQuestionDate(question);
      let format = {
        month: questionDate.date.month,
      };

      const data = JSON.parse(question.answers[0].text);
      // if there appears to be a d for "day" in the formatting string, show the day selector
      if (data['date_format'].match(/d/)) {
        format.day = questionDate.date.day;
      }

      format.year = questionDate.date.year;

      return format;
    },
    dateValid(question) {
      const questionDate = this.getQuestionDate(question);
      if (questionDate.date.month === 0) return false;
      if (questionDate.date.year === 0) return false;

      // only check day if formatting string includes it
      const data = JSON.parse(question.answers[0].text);
      if (questionDate.date.day === 0 && data['date_format'].match(/d/)) {
        return false;
      }

      return true;
    },
    getQuestionDate(question) {
      let questionDate = this.dates.find(d => d.questionId === question.id);
      if (!questionDate) {
        questionDate = {
          questionId: question.id,
          date: {
            day: 0,
            month: 0,
            year: 0,
          },
        };
      }
      return questionDate;
    },
    isEmpty(question) {
      const isStart = question.id === 'start';
      const isEnd = question.id === 'end';
      const isCompleted = this.clippedQuestions[1]?.id === 'completed';
      return isStart || (isEnd && isCompleted);
    },
    handleBack() {
      this.$emit('back-clicked');
    },
    isAnswerSelected(answer) {
      return this.selectedAnswerIds.includes(answer.id);
    },
    resetState() {
      this.dates = [];
      this.selectedAnswerIds = [];
    },
    showBack(question) {
      if (!this.allowBack || !question) {
        return false;
      }
      const isCurrentQuestion = question.id === this.clippedQuestions[1].id;
      const isFirstQuestion = this.clippedQuestions[0].id === 'start';
      return isCurrentQuestion && !isFirstQuestion;
    },
    sortedAnswers(question) {
      return [...question.answers].sort((a, b) => a.sortOrder - b.sortOrder);
    },
  },
};
</script>

<style lang="scss" scoped>
.questionnaire-component-container {
  .content-container {
    align-items: center;
    box-sizing: border-box;
    display: flex;
    flex: 1;
    justify-content: center;
    position: relative;

    .back {
      background: transparent;
      padding: 0;
      position: absolute;
      width: 20px;
    }

    .content {
      // max height required for ie11
      max-height: 100%;
      min-width: 65%;
    }

    .box {
      align-items: center;
      background-color: white;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100%;
      justify-content: center;
      margin: auto;
      max-width: rem-calc(500);
      width: 100%;

      @include breakpoint(small) {
        min-height: 0px;
        padding: rem-calc(32) rem-calc(16);
      }

      &.empty {
        display: none;
      }

      .question {
        margin-bottom: rem-calc(16);
        // must have width for ie11
        width: 100%;

        p {
          margin: 0;
        }
      }

      .date-submit {
        margin-top: rem-calc(16);
      }

      .answer-container {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        margin-top: rem-calc(16);
        width: 100%;

        input {
          display: none;

          + label {
            border: 2px solid #6f0079;
            border-radius: 24px;
            color: #6f0079;
            cursor: pointer;
            flex-basis: calc(50% - 44px);
            font-weight: $semi-bold;
            height: 44px;
            margin: 8px;
            padding: 0 12px;

            @include breakpoint(small) {
              height: 42px;
              margin: 6px;
              padding: 0 10px;
            }

            .answer-text {
              align-items: center;
              display: flex;
              height: 100%;
              justify-content: center;
              font-size: 14px;

              @include breakpoint(small) {
                font-size: 13px;
              }

              p {
                margin: 0;
              }
            }
          }

          &:checked {
            + label {
              background: #6f0079;
              color: white;
            }
          }
        }

        &.single label {
          flex-basis: 100%;
        }
      }
    }
  }
}
</style>
